import { Container, ITextStyle, Text, TextStyleAlign } from "pixi.js"

export type TextSize = "normal" | "tiny" | "small" | "medium" | "large"

export interface TextProps {
    size?: TextSize
}

export type TextVariant = "LogoFont" | "BannerFont"

export class TextRenderer {
    _color: number
    _namedSize: TextSize
    _size: number
    _align: TextStyleAlign
    _fontFamily: string
    _bold: boolean
    _resolution?: number
    _wordWrapWidth?: number
    _strokeThickness?: number

    private namedSizeToNumberSize = (size?: TextSize): number => {
        if (size === "tiny") {
            return 8
        }
        if (size === "small") {
            return 12
        }
        if (size === "medium") {
            return 14
        }
        if (size === "normal") {
            return 16
        }

        return this.namedSizeToNumberSize("medium")
    }

    size = (size: TextSize): TextRenderer => {
        this._namedSize = size
        return this
    }

    color = (color: number): TextRenderer => {
        if (color !== undefined) {
            this._color = color
        }
        return this
    }

    bold = (): TextRenderer => {
        this._bold = true
        return this
    }

    align = (align: TextStyleAlign): TextRenderer => {
        this._align = align
        return this
    }

    fontFamily = (fontFamily: string): TextRenderer => {
        this._fontFamily = fontFamily
        return this
    }

    fontSize = (size: number): TextRenderer => {
        this._size = size
        return this
    }

    // Wrap onto multiple lines at `width`, breaking inside a word if one is longer than that. Set
    // at BUILD time rather than by poking text.style afterwards, so the measured width of the
    // resulting Text is guaranteed to respect it — a caller that sizes a panel around the text
    // (see gui/roster_speech.ts) depends on that being true from the first frame.
    wordWrap = (width: number): TextRenderer => {
        this._wordWrapWidth = width
        return this
    }

    // Outline width, in pixels, of the black stroke every Text gets by default (see render()). The
    // default earns its keep over the world, where a label has to stay readable against whatever
    // tile it happens to sit on -- but it is a liability on flat UI: a DARK glyph on a LIGHT fill
    // grows a black halo the same colour as its own ink, which fattens the stems and closes up the
    // counters of small bold type. Pass 0 for text that sits on a solid panel.
    strokeThickness = (thickness: number): TextRenderer => {
        this._strokeThickness = thickness
        return this
    }

    variant = (variant: TextVariant | string): TextRenderer => {
        if (variant) {
            if (variant === "BannerFont") {
                this._color = 0xffffff
                this._size = 20
            }
            if (variant === "LogoFont") {
                this._color = 0xffffff
                this._size = 40
            }
            if (variant === "RedChatFont") {
                this._color = 0xffa500
                this._size = 10
            }
            if (variant === "SmallFont") {
                this._size = 10
            }
            if (variant === "AlertFont") {
                this._color = 0xff0000
                this._size = 30
            }
            if (variant === "DamageFont") {
                this._color = 0xff0000
                this._size = 15
                this._bold = true
            }
            if (variant === "EntityImportantFont") {
                this._size = 15
                this._bold = true
            }
            if (variant === "ChatFont") {
                this._color = 0x00ee00
                this._size = 10
            }
            if (variant === "BubbleChatFont") {
                this._color = 0xffffff
                this._size = this.namedSizeToNumberSize("small")
                // Pin the resolution so the Text doesn't AUTO-re-rasterize when it's first drawn into
                // the follow-cam PiP's higher-resolution RenderTexture — that re-raster is what made a
                // PiP bubble "jump" on its first frame. Match the PiP texture's 2×devicePixelRatio so
                // it stays crisp there too.
                this._resolution = 2 * (typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1)
            }
            if (variant === "Normal-Green-Bold") {
                this._color = 0x00ff00
                this._size = 15
            }
            if (variant === "ImportantFont") {
                this._color = 0xffffff
                this._size = 30
            }
            if (variant === "GiganticFont") {
                this._color = 0xffffff
                this._size = 100
            }
        }
        return this
    }

    render = (text?: string): Container => {
        const style: Partial<ITextStyle> = {
            fontFamily: this._fontFamily || "Verdana, Geneva, sans-serif",
            // font: 'bold italic Sans-Serif',
            fill: "#ffffff",
            // stroke: '#000000',
            fontWeight: this._bold ? "bolder" : "normal",
            align: this._align || "left",
            strokeThickness: this._strokeThickness === undefined ? 1 : this._strokeThickness,
            fontSize: this._size === undefined ? this.namedSizeToNumberSize(this._namedSize) : this._size,
            ...(this._wordWrapWidth !== undefined
                ? { wordWrap: true, wordWrapWidth: this._wordWrapWidth, breakWords: true }
                : {}),
        }

        var someText = new Text(text || "", style)
        someText.roundPixels = true
        if (this._resolution !== undefined) {
            // Fixed resolution => Text.autoResolution off => it won't re-rasterize (nor glitch for a
            // frame) when rendered into a RenderTexture of a different resolution, i.e. the PiP.
            someText.resolution = this._resolution
        }
        someText.tint = this._color !== undefined ? this._color : 0xffffff
        someText.scale.set(1.0)

        return someText
    }
}
